import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

const withServerError = (fn) => async (arg, { rejectWithValue }) => {
  try {
    return await fn(arg);
  } catch (error) {
    return rejectWithValue(
      error.response?.data || { success: false, message: error.message }
    );
  }
};

export const conditionBody = (res) => res?.payload?.data ?? res?.payload ?? null;

export const GetPropertyCondition = createAsyncThunk(
  "propertyCondition/get",
  withServerError(({ tenancyRequestId }) =>
    axios.get(`${BASE_URL}/property-condition/${tenancyRequestId}`)
  )
);

// Resolves a tenancy's stable id from tenantId+flatId, so callers don't need
// to carry FlatInterestRequest._id around everywhere.
export const ResolveTenancyCondition = createAsyncThunk(
  "propertyCondition/resolve",
  withServerError(({ tenantId, flatId }) =>
    axios.get(`${BASE_URL}/property-condition/resolve/${tenantId}/${flatId}`)
  )
);

export const UploadMoveInPhotos = createAsyncThunk(
  "propertyCondition/uploadMoveInPhotos",
  withServerError(({ tenancyRequestId, photos }) => {
    const formData = new FormData();
    photos.forEach((file, i) => formData.append(`photo${i + 1}`, file));
    return axios.post(
      `${BASE_URL}/property-condition/${tenancyRequestId}/move-in-photos`,
      formData
    );
  })
);

export const SubmitDamageClaim = createAsyncThunk(
  "propertyCondition/submitClaim",
  withServerError(({ tenancyRequestId, description, claimedAmount, photos }) => {
    const formData = new FormData();
    formData.append("description", description);
    formData.append("claimedAmount", claimedAmount);
    photos.forEach((file, i) => formData.append(`photo${i + 1}`, file));
    return axios.post(
      `${BASE_URL}/property-condition/${tenancyRequestId}/claim`,
      formData
    );
  })
);

export const RespondToDamageClaim = createAsyncThunk(
  "propertyCondition/respond",
  withServerError(({ id, message }) =>
    axios.post(`${BASE_URL}/property-condition/${id}/respond`, { message })
  )
);

export const FinalizeDamageClaim = createAsyncThunk(
  "propertyCondition/finalize",
  withServerError(({ id, finalAmount, note }) =>
    axios.post(`${BASE_URL}/property-condition/${id}/finalize`, { finalAmount, note })
  )
);

const PropertyConditionSlice = createSlice({
  name: "propertyCondition",
  initialState: {
    loading: false,
    condition: null,
    tenancyRequestId: null,
    moveInStatus: null,
    error: "",
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GetPropertyCondition.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(GetPropertyCondition.fulfilled, (state, action) => {
        state.loading = false;
        state.condition = action.payload?.data?.data || null;
      })
      .addCase(GetPropertyCondition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load property condition";
      })
      .addCase(ResolveTenancyCondition.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(ResolveTenancyCondition.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data?.data;
        state.tenancyRequestId = data?.tenancyRequestId || null;
        state.moveInStatus = data?.moveInStatus || null;
        state.condition = data?.condition || null;
      })
      .addCase(ResolveTenancyCondition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to resolve tenancy";
      })
      .addMatcher(
        (action) =>
          [
            UploadMoveInPhotos.fulfilled.type,
            SubmitDamageClaim.fulfilled.type,
            RespondToDamageClaim.fulfilled.type,
            FinalizeDamageClaim.fulfilled.type,
          ].includes(action.type),
        (state, action) => {
          const updated = action.payload?.data?.data;
          if (updated) state.condition = updated;
        }
      );
  },
});

export default PropertyConditionSlice.reducer;
